import {
  CMRDamage,
  CMRVehicle,
  EditCMR,
  InstallmentsEnum,
  OrderDetail,
  OrderModify,
  OrderStatus,
  RequestCMR
} from '@insurance/api/requests'
import { deviceSession, formatDateToISOFormat } from '@shared/utils'
import { NamePath } from 'antd/es/form/interface'
import dayjs from 'dayjs'
import { TFunction } from 'i18next'

import { CmrFormValues } from './type'
import { getCustomerFields, getMixtContractorFields } from '../PersonsFields'

export const CMR_FORM_DATA_KEY = '__cmr_insurance_form_data__'

export const saveCmrValuesToStorage = (
  values: CmrFormValues,
  id: string | number = ''
) => {
  sessionStorage.setItem(id + CMR_FORM_DATA_KEY, JSON.stringify(values))
}

export const getCmrValuesFromStorage = (id: string | number = '') => {
  const values = sessionStorage.getItem(id + CMR_FORM_DATA_KEY)

  if (!values) return

  const parsedValues = JSON.parse(values)
  const {
    validFromProps,
    effectiveDateProps,
    contractorActivitySinceProps,
    vehiclesProps,
    lastDamagesProps
  } = formProps()

  return {
    ...parsedValues,
    ...(parsedValues[validFromProps.name] && {
      [validFromProps.name]: dayjs(parsedValues[validFromProps.name])
    }),
    ...(parsedValues[effectiveDateProps.name] && {
      [effectiveDateProps.name]: dayjs(parsedValues[effectiveDateProps.name])
    }),
    ...(parsedValues[contractorActivitySinceProps.name] && {
      [contractorActivitySinceProps.name]: dayjs(
        parsedValues[contractorActivitySinceProps.name]
      )
    }),
    ...(parsedValues[vehiclesProps.name] && {
      [vehiclesProps.name]: parsedValues[vehiclesProps.name]?.map(
        (vehicle: CMRVehicle) => ({
          ...vehicle,
          year: vehicle?.year ? dayjs(vehicle?.year) : undefined
        })
      )
    }),
    ...(parsedValues[vehiclesProps.name] && {
      [vehiclesProps.name]: parsedValues[vehiclesProps.name]?.map(
        (vehicle: CMRVehicle) => ({
          ...vehicle,
          year: vehicle?.year ? dayjs(vehicle?.year) : undefined
        })
      )
    }),
    ...(parsedValues[lastDamagesProps.name] && {
      [lastDamagesProps.name]: parsedValues[lastDamagesProps.name]?.map(
        (damage: CMRDamage) => ({
          ...damage,
          date: damage?.date ? dayjs(damage?.date) : undefined
        })
      )
    })
  }
}

export const removeCmrValuesFromStorage = (id: string | number = '') =>
  sessionStorage.removeItem(id + CMR_FORM_DATA_KEY)

export const formProps = (tFunction?: TFunction) => {
  const t = tFunction ? tFunction : () => ''

  return {
    validFromProps: { name: 'valid_from', label: t('valid_from') },
    periodProps: { name: 'period', label: t('period') },
    installmentsProps: { name: 'installments', label: t('installments') },
    totalAmountProps: {
      name: 'total_amount',
      label: t('total_insured_amount')
    },
    perCaseAmountProps: {
      name: 'per_case_amount',
      label: t('per_case_amount')
    },
    currencyAmountProps: {
      name: 'currency',
      label: t('currency')
    },
    generalCargoProps: { name: 'general_cargo', label: t('general_cargo') },
    highRiskCargoProps: {
      name: 'high_risk_cargo',
      label: t('high_risk_cargo')
    },
    risksProps: { name: 'risks', label: t('risks') },
    employeeErrorsProps: {
      name: 'employee_errors',
      label: t('employee_errors')
    },
    additionalExpensesProps: {
      name: 'additional_expenses',
      label: t('additional_expenses')
    },
    franchiseProps: {
      name: 'franchise',
      label: t('franchise')
    },
    contractorTransportLicense: {
      name: 'contractor_transport_license',
      label: t('contractor_transport_license')
    },
    contractorActivitySinceProps: {
      name: 'contractor_activity_since',
      label: t('contractor_activity_since')
    },
    contractorActivityTypeProps: {
      name: 'contractor_activity_type',
      label: t('contractor_activity_type')
    },
    destinationsProps: { name: 'destinations', label: t('destinations') },
    tripsPerVehicleProps: {
      name: 'trips_per_vehicle',
      label: t('trips_per_vehicle')
    },
    generalCargoTypesProps: {
      name: 'general_cargo_types',
      label: t('general_cargo_types')
    },
    specialCargoTypesProps: {
      name: 'special_cargo_types',
      label: t('special_cargo_types')
    },
    vehiclesProps: { name: 'vehicles', label: t('vehicles') },
    lastDamagesProps: {
      name: 'last_3_years_damages',
      label: t('last_3_years_damages')
    },
    contractorHeadProps: {
      name: 'contractor_head',
      label: t('contractor_head')
    },
    contractorContactRoleProps: {
      name: 'contractor_contact_role',
      label: t('contractor_contact_role')
    },
    contractorCompanyNameProps: {
      name: ['contractor', 'legal_entity', 'name'],
      label: t('company_name')
    },
    contractorProps: { name: 'contractor', label: t('contractor') },
    customerProps: { name: 'customer', label: t('customer') },

    effectiveDateProps: {
      name: 'effective_date',
      label: t('effective_date')
    },

    // UI-only
    noDamagesProps: { name: 'no_damages', label: t('no_damages_last_3_years') },

    modifyReasonProps: {
      name: 'modify_reason',
      label: t('modify_reason')
    },

    // Hidden
    stepKey: { name: 'step' },
    priceKey: { name: 'price' },
    orderKey: { name: 'order_id' },
    setBeneficiaryKey: {
      name: 'set_beneficiary_data'
    }
  } satisfies Record<string, { name: NamePath; label?: React.ReactNode }>
}

export const normalizeCmrData = (
  values: CmrFormValues,
  useSession = true
): RequestCMR => {
  const session = deviceSession()

  return {
    ...values,
    // strip UI-only / form-helper fields
    step: undefined,
    price: undefined,
    order_id: undefined,
    no_damages: undefined,
    customer: undefined,
    effective_date: undefined,
    installments: InstallmentsEnum._1,
    contractor_activity_since: values.contractor_activity_since
      ? Number(dayjs(values.contractor_activity_since).format('YYYY'))
      : undefined,
    valid_from: formatDateToISOFormat(values.valid_from)!,
    last_3_years_damages:
      !values.no_damages && values.last_3_years_damages?.length
        ? values.last_3_years_damages.map((damage) => ({
            ...damage,
            date: formatDateToISOFormat(damage?.date),
            amount: String(damage?.amount)
          }))
        : undefined,
    vehicles: values.vehicles?.map((vehicle) => ({
      ...vehicle,
      year: vehicle?.year
        ? Number(dayjs(vehicle?.year).format('YYYY'))
        : undefined
    })),
    ...getMixtContractorFields(values),
    ...(useSession && {
      session: {
        device_id: session.deviceId,
        session_id: session.sessionId
      }
    })
  } as RequestCMR
}

export const normalizeOrderData = (values: CmrFormValues): EditCMR => {
  const base = normalizeCmrData(values, false)

  return {
    ...base,
    insured_same_as_contractor: true,
    customer: values.customer ? getCustomerFields(values.customer) : undefined
  } as EditCMR
}

export const normalizeModifyOrderData = (
  values: CmrFormValues
): OrderModify => {
  const data = normalizeOrderData(values)

  return {
    cmr: {
      effective_date: formatDateToISOFormat(values.effective_date),
      insured_same_as_contractor: data.insured_same_as_contractor,
      insured_person: data.insured_person,
      refund_destination: values.refund_destination,
      modify_reason: values.modify_reason,
      contractor: data.contractor,
      customer: data.customer,
      total_amount: data.total_amount,
      per_case_amount: data.per_case_amount,
      general_cargo: data.general_cargo,
      high_risk_cargo: data.high_risk_cargo,
      risks: data.risks,
      vehicles: data.vehicles,
      employee_errors: data.employee_errors,
      additional_expenses: data.additional_expenses,
      contractor_head: data.contractor_head,
      contractor_contact_role: data.contractor_contact_role,
      contractor_activity_since: data.contractor_activity_since,
      contractor_activity_type: data.contractor_activity_type,
      contractor_transport_license: data.contractor_transport_license,
      destinations: data.destinations,
      trips_per_vehicle: data.trips_per_vehicle,
      general_cargo_types: data.general_cargo_types,
      special_cargo_types: data.special_cargo_types,
      last_3_years_damages: data.last_3_years_damages,
      franchise: data.franchise
    }
  } as OrderModify
}

export const orderParentOrChildId = (order?: OrderDetail) =>
  !!order?.status &&
  [OrderStatus.COMPLETED, OrderStatus.PARTIAL_PAID].includes(order.status)
